import { CheckIcon } from "@radix-ui/react-icons";
import {
  OpenAPI,
  PublicFormsService,
  PublicFormView,
  InquirySubmitRequest,
} from "client/openapi";
import { errorText } from "components/Forms/formsApi";
import PublicForm, { PublicFormAnswers } from "components/Forms/PublicForm";
import { ReactNode, RefObject, useEffect, useRef, useState } from "react";
import { Route, Routes, useParams } from "react-router-dom";
import { askWebsiteToShowTop, EMBED_HEIGHT_MESSAGE } from "util/embed";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const TURNSTILE_SCRIPT =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const ROBOT_CHECK = "Please confirm you're not a robot, then submit again.";

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        options: Record<string, unknown>
      ) => string;
      remove: (widgetId: string) => void;
    };
  }
}

/** Inside another website's page (the embed code), rather than on its own. */
function isFramed(): boolean {
  return window.self !== window.top;
}

/**
 * In a frame, tell the page around it how tall the form is, so the embed
 * code can size the frame to fit with no inner scrolling. The website shows
 * through around the form's card.
 */
function usePostHeight(box: RefObject<HTMLElement>, framed: boolean) {
  useEffect(() => {
    const element = box.current;
    if (!framed || !element) return;
    document.body.style.overflowY = "auto";
    document.body.style.background = "transparent";
    const post = () =>
      window.parent.postMessage(
        {
          type: EMBED_HEIGHT_MESSAGE,
          height: Math.ceil(element.getBoundingClientRect().height),
        },
        "*"
      );
    const observer = new ResizeObserver(post);
    observer.observe(element);
    post();
    return () => observer.disconnect();
  }, [box, framed]);
}

let turnstileScript: Promise<void> | undefined;

function loadTurnstile(): Promise<void> {
  if (!turnstileScript) {
    turnstileScript = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = TURNSTILE_SCRIPT;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        turnstileScript = undefined;
        reject(new Error("Turnstile did not load"));
      };
      document.head.appendChild(script);
    });
  }
  return turnstileScript;
}

/** Cloudflare's robot check. A pass is good for one try, so each try gets a new one. */
function RobotCheck({
  siteKey,
  round,
  error,
  onToken,
}: {
  siteKey: string;
  round: number;
  error?: string;
  onToken: (token: string | null) => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const tokenTo = useRef(onToken);
  tokenTo.current = onToken;
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let widget: string | undefined;
    let gone = false;
    tokenTo.current(null);
    loadTurnstile()
      .then(() => {
        if (gone || !box.current || !window.turnstile) return;
        widget = window.turnstile.render(box.current, {
          sitekey: siteKey,
          callback: (token: string) => tokenTo.current(token),
          "expired-callback": () => tokenTo.current(null),
          "error-callback": () => tokenTo.current(null),
        });
      })
      .catch(() => !gone && setFailed(true));
    return () => {
      gone = true;
      if (widget && window.turnstile) window.turnstile.remove(widget);
    };
  }, [siteKey, round]);

  return (
    <div className="mt-6">
      <div ref={box} />
      {failed && (
        <p className="mt-1 text-sm text-red-600" data-form-error>
          The robot check could not load. Check your connection and reload the
          page.
        </p>
      )}
      {error && !failed && (
        <p className="mt-1 text-sm text-red-600" data-form-error>
          {error}
        </p>
      )}
    </div>
  );
}

function Message({
  title,
  text,
  done = false,
}: {
  title: string;
  text?: string;
  done?: boolean;
}) {
  return (
    <div className="py-8 text-center">
      {done && (
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-green-500 text-white">
          <CheckIcon width={28} height={28} />
        </div>
      )}
      <h1 className="text-2xl font-semibold text-gray-900">{title}</h1>
      {text && <p className="mt-3 text-base text-gray-600">{text}</p>}
    </div>
  );
}

/**
 * A website inquiry form: a business's "contact us" form, on its own page or
 * in a frame on the business's website. Whoever fills it out is added as a
 * new family. No login.
 */
function InquiryFormPage() {
  const { publicId } = useParams();
  const framed = isFramed();
  const box = useRef<HTMLDivElement>(null);
  const [view, setView] = useState<PublicFormView>();
  const [loadFailed, setLoadFailed] = useState(false);
  const [saved, setSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<string>();
  // A field people never see; a bot that fills it in is not saved.
  const [website, setWebsite] = useState("");
  const [robotToken, setRobotToken] = useState<string | null>(null);
  const [robotRound, setRobotRound] = useState(0);

  usePostHeight(box, framed);

  useEffect(() => {
    if (!publicId) return;
    PublicFormsService.loadInquiryForm({ publicId })
      .then(setView)
      .catch(() => setLoadFailed(true));
  }, [publicId]);

  // Bring the first marked answer into view after a refused submit.
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      document
        .querySelector("[data-form-error]")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [errors]);

  /**
   * The top of the form. In a frame, the website's page scrolls too: by
   * scrollIntoView in Chrome, and by the embed code where WebKit doesn't
   * carry it up.
   */
  function toTop() {
    box.current?.scrollIntoView({ block: "start" });
    if (framed) askWebsiteToShowTop();
  }

  function submit(answers: PublicFormAnswers) {
    if (!publicId) return;
    if (TURNSTILE_SITE_KEY && !robotToken) {
      setErrors({ captcha: ROBOT_CHECK });
      return;
    }
    setSubmitting(true);
    setNotice(undefined);
    const request: InquirySubmitRequest = {
      revision: answers.revision,
      parent: answers.parent,
      // Everyone is new: each student is one the parent added.
      students: (answers.students || [])
        .filter((entry) => entry.student_id == null)
        .map((entry) => entry.values || {}),
      terms_agreed: answers.terms_agreed,
      website: website || null,
      turnstile_token: robotToken,
    };
    PublicFormsService.submitInquiryForm({ publicId, requestBody: request })
      .then((result) => {
        if (result.ok) {
          setSaved(true);
          toTop();
          return;
        }
        setRobotRound((round) => round + 1);
        if (result.state === "errors") {
          setErrors(result.errors || {});
        } else if (result.state === "form_updated" && result.form) {
          setErrors({});
          setView(result.form);
          setNotice(
            "This form was just updated. Please check your answers and submit again."
          );
          toTop();
        } else {
          setView({ state: PublicFormView.state.INVALID });
        }
      })
      .catch((e) => {
        setRobotRound((round) => round + 1);
        // The notice is at the top of the form; bring it into view.
        setNotice(
          errorText(e, "Could not send. Check your connection and try again.")
        );
        toTop();
      })
      .finally(() => setSubmitting(false));
  }

  let content: ReactNode;
  if (loadFailed) {
    content = (
      <Message
        title="This form could not be opened."
        text="Check your connection and reload the page."
      />
    );
  } else if (!publicId || view?.state === PublicFormView.state.INVALID) {
    content = <Message title="This form is no longer available." />;
  } else if (!view) {
    content = (
      <div className="animate-pulse space-y-4 py-4">
        <div className="h-4 w-1/3 rounded bg-gray-200" />
        <div className="h-7 w-2/3 rounded bg-gray-200" />
        <div className="h-11 rounded bg-gray-100" />
        <div className="h-11 rounded bg-gray-100" />
      </div>
    );
  } else if (saved) {
    content = (
      <Message
        done
        title="Thank you!"
        text={
          view.org_name
            ? `${view.org_name} will be in touch soon.`
            : "We'll be in touch soon."
        }
      />
    );
  } else {
    content = (
      <>
        <div
          aria-hidden="true"
          className="absolute -left-[9999px] top-0 h-px w-px overflow-hidden"
        >
          <label>
            Website
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </label>
        </div>
        <PublicForm
          view={view}
          submitting={submitting}
          errors={errors}
          notice={notice}
          onSubmit={submit}
          beforeSubmit={
            TURNSTILE_SITE_KEY ? (
              <RobotCheck
                siteKey={TURNSTILE_SITE_KEY}
                round={robotRound}
                error={errors["captcha"]}
                onToken={setRobotToken}
              />
            ) : (
              errors["captcha"] && (
                <p className="mt-6 text-sm text-red-600" data-form-error>
                  {errors["captcha"]}
                </p>
              )
            )
          }
        />
      </>
    );
  }

  return (
    <div
      ref={box}
      className={framed ? "p-1" : "min-h-screen bg-gray-50 px-4 py-8 sm:py-12"}
    >
      <div
        className={`relative mx-auto w-full max-w-[560px] rounded-2xl bg-white p-6 sm:p-8 ${
          framed ? "border border-gray-200" : "shadow-sm"
        }`}
      >
        {content}
      </div>
    </div>
  );
}

/**
 * The app for /f/inquiry/<id>. It runs without the login (no Auth0), so a
 * visitor who is signed in to Tadpole never makes a sign-in call from a
 * business's website, and nothing about their account is loaded there.
 */
export default function InquiryApp() {
  OpenAPI.BASE = process.env.REACT_APP_BACKEND_API_DOMAIN || "";
  return (
    <Routes>
      <Route path="/f/inquiry/:publicId" element={<InquiryFormPage />} />
      <Route path="*" element={<InquiryFormPage />} />
    </Routes>
  );
}
